import { Controller } from "@hotwired/stimulus"

// The /admin/vocal editor: variables inserted at the caret, the test call's
// place picked from an autocomplete over the city's places (photo, name,
// neighbourhood; filtered here, no request) and switched without leaving
// the page (the frame and the form's hidden
// fields follow, so a save comes back to the same place), and a warning
// before an unsaved prompt is lost.
export default class extends Controller {
  static targets = ["form", "prompt", "save", "search", "results", "places", "frame", "open", "city", "venue"]

  connect() {
    this.places = JSON.parse(this.placesTarget.textContent)
    this.current = this.places.find((p) => p.id === this.venueTarget.value)
    this.changed = false
    this.guard = (e) => { if (this.changed) { e.preventDefault(); e.returnValue = "" } }
    window.addEventListener("beforeunload", this.guard)
  }

  disconnect() { window.removeEventListener("beforeunload", this.guard) }

  dirty() {
    this.changed = true
    this.saveTarget.textContent = "Save & push •"
  }

  saving() {
    this.changed = false
    this.saveTarget.disabled = true
    this.saveTarget.textContent = "Pushing…"
  }

  leave(e) {
    if (this.changed && !confirm("Unsaved changes to the agent will be lost. Leave?")) e.preventDefault()
    else this.changed = false
  }

  insert(e) {
    const ta = this.promptTarget, text = e.currentTarget.dataset.var
    const start = ta.selectionStart, end = ta.selectionEnd
    ta.setRangeText(text, start, end, "end")
    ta.focus()
    this.dirty()
  }

  open() {
    this.searchTarget.select()
    this.render(this.places)
  }

  filter() {
    const q = this.fold(this.searchTarget.value.trim())
    this.render(q ? this.places.filter((p) => this.fold(`${p.name} ${p.hood}`).includes(q)) : this.places)
  }

  fold(s) { return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() }

  render(list) {
    this.shown = list.slice(0, 80)
    this.active = 0
    this.resultsTarget.replaceChildren(...this.shown.map((p, i) => this.row(p, i)))
    this.resultsTarget.classList.toggle("hidden", this.shown.length === 0)
    this.highlight()
  }

  row(p, i) {
    const b = document.createElement("button")
    b.type = "button"
    b.className = "w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
    b.addEventListener("mousedown", (e) => { e.preventDefault(); this.pick(p) })
    b.addEventListener("mouseenter", () => { this.active = i; this.highlight() })
    const img = document.createElement(p.photo ? "img" : "span")
    img.className = "w-10 h-10 rounded-lg object-cover bg-gray-100 shrink-0"
    if (p.photo) { img.src = p.photo; img.loading = "lazy"; img.alt = "" }
    const text = document.createElement("span")
    text.className = "min-w-0"
    const name = document.createElement("span")
    name.className = "block text-sm font-medium text-gray-900 truncate"
    name.textContent = p.name
    const hood = document.createElement("span")
    hood.className = "block text-xs text-gray-500 truncate"
    hood.textContent = p.hood
    text.append(name, hood)
    b.append(img, text)
    return b
  }

  highlight() {
    [...this.resultsTarget.children].forEach((el, i) => el.classList.toggle("bg-gray-50", i === this.active))
    this.resultsTarget.children[this.active]?.scrollIntoView({ block: "nearest" })
  }

  key(e) {
    if (this.resultsTarget.classList.contains("hidden")) return
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      const n = this.shown.length
      this.active = (this.active + (e.key === "ArrowDown" ? 1 : n - 1)) % n
      this.highlight()
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (this.shown[this.active]) this.pick(this.shown[this.active])
    } else if (e.key === "Escape") {
      this.close()
    }
  }

  outside(e) {
    if (!this.searchTarget.parentElement.contains(e.target)) this.close()
  }

  close() {
    this.resultsTarget.classList.add("hidden")
    if (this.current) this.searchTarget.value = this.current.name
  }

  pick(p) {
    this.current = p
    this.close()
    this.searchTarget.blur()
    this.venueTarget.value = p.id
    if (!this.hasFrameTarget) return
    const url = this.frameTarget.dataset.base + p.id
    this.frameTarget.src = url
    if (this.hasOpenTarget) this.openTarget.href = url
    const u = new URL(window.location.href)
    u.searchParams.set("city", this.cityTarget.value)
    u.searchParams.set("venue", p.id)
    history.replaceState(history.state, "", u)
  }

  reload() {
    if (this.hasFrameTarget) this.frameTarget.src = this.frameTarget.src
  }
}
